import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// react-router components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Breadcrumbs from "../../Breadcrumbs";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "./styles";

import {
  useMaterialUIController,
  setMiniSidenav,
  setSidenavPinned,
  setOpenConfigurator,
} from "../../../context";

import authService from "../../../services/authService";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import ThemeModeToggle from "../../../components/ThemeModeToggle";
import UserProfileMenu from "../../../components/UserProfileMenu";
import routes from "../../../routes";

function DashboardNavbar({ absolute = false, light = false, isMini = false, hidden = false }) {
  const { t } = useTranslation();
  const [navbarType, setNavbarType] = useState<"static" | "sticky">("static");
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const theme = useTheme();
  const isCompactNav = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery("(max-width:991.98px)");
  const location = useLocation();
  const route = location.pathname.split("/").slice(1);
  const pageTitle = useMemo(() => {
    const matched = (routes as Array<{ route?: string; name?: string; nameKey?: string }>).find((entry) => {
      if (!entry.route) return false;
      if (entry.route.includes(":")) {
        const pattern = new RegExp(`^${entry.route.replace(/:[^/]+/g, "[^/]+")}$`);
        return pattern.test(location.pathname);
      }
      return entry.route === location.pathname;
    });
    if (matched?.nameKey) return t(matched.nameKey);
    return matched?.name || route[route.length - 1] || t("common.home");
  }, [location.pathname, route, t]);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Get user information
    const getUserInfo = async () => {
      try {
        const userInfo = await authService.getUser();
        setUser(userInfo);
      } catch (error) {
        console.error("Error getting user info:", error);
      }
    };

    getUserInfo();
  }, []);


  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // Navbar transparency handling is disabled to keep navbar always visible
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => {
    if (isMobile) {
      setMiniSidenav(dispatch, !miniSidenav);
      return;
    }
    setSidenavPinned(dispatch, miniSidenav);
  };
  
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const profile = user?.profile as
    | { given_name?: string; family_name?: string; name?: string }
    | undefined;
  const givenName = profile?.given_name?.trim() || "";
  const familyName = profile?.family_name?.trim() || "";
  const displayName =
    [givenName, familyName].filter(Boolean).join(" ") ||
    (profile?.name?.trim() && !profile.name.includes("@") ? profile.name.trim() : "") ||
    "User";
  const initials =
    givenName && familyName
      ? `${givenName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
      : displayName
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part.charAt(0))
          .join("")
          .toUpperCase() || "U";

  // If hidden prop is true, don't render the navbar
  if (hidden) {
    return null;
  }

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, text },
  }: any) => ({
    color: darkMode ? "#ffffff !important" : `${(text?.main || dark.main)} !important`,
  });

  const touchButtonSx = {
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: 1.5,
    border: 1,
    borderColor: "divider",
  };

  const menuButton = (
    <IconButton
      size="small"
      disableRipple
      color="inherit"
      aria-label={t("sidenav.menu")}
      sx={isCompactNav ? touchButtonSx : navbarMobileMenu}
      onClick={handleMiniSidenav}
    >
      <Icon sx={iconsStyle} fontSize="medium">
        {isMobile ? (miniSidenav ? "menu" : "close") : (miniSidenav ? "menu_open" : "menu")}
      </Icon>
    </IconButton>
  );

  const settingsButton = (
    <IconButton
      size="small"
      disableRipple
      color="inherit"
      aria-label={t("configurator.title")}
      sx={isCompactNav ? touchButtonSx : navbarIconButton}
      onClick={handleConfiguratorOpen}
    >
      <Icon sx={iconsStyle}>settings</Icon>
    </IconButton>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
        flexShrink: 0,
      })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {isCompactNav ? (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 1.5, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, minHeight: 44 }}>
              {menuButton}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Breadcrumbs icon="home" title={pageTitle} light={darkMode} darkMode={darkMode} compact />
              </Box>
              {user ? (
                <UserProfileMenu displayName={displayName} initials={initials} presentation="button" />
              ) : null}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <LanguageSwitcher iconOnly />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <ThemeModeToggle touch />
                {settingsButton}
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              color="inherit"
              sx={(theme: any) => ({
                ...navbarRow(theme, { isMini }),
                justifyContent: "flex-start",
                alignItems: "center",
                height: "100%",
                mb: 0,
              })}
            >
              <Breadcrumbs icon="home" title={pageTitle} light={darkMode} darkMode={darkMode} />
            </Box>
            <Box
              sx={(theme: any) => ({
                ...navbarRow(theme, { isMini }),
                ml: { md: "auto" },
                justifyContent: "flex-end",
              })}
            >
              <Box
                color={light ? "white" : "inherit"}
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{ ml: "auto" }}
              >
                {user ? <UserProfileMenu displayName={displayName} initials={initials} /> : null}
                <LanguageSwitcher compact />
                {menuButton}
                <ThemeModeToggle />
                {settingsButton}
              </Box>
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  hidden: PropTypes.bool,
};

export default DashboardNavbar;
